import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { Field } from "formik";
import { FileField } from "seed/helpers";
import Table from "components/collections/Table";
import l from "components/util/l";

import lazyCall from "components/util/helpers/LazyCall";
const loadCSVFromUrl = lazyCall("loadCSVFromUrl", "components/collections/util/excel");
const loadWorkbookFromUrl = lazyCall("loadWorkbookFromUrl", "components/collections/util/excel");
const getSheetNames = lazyCall("getSheetNames", "components/collections/util/excel");
const parseSheetToCSV = lazyCall("parseSheetToCSV", "components/collections/util/excel");
const parseCSVToCollection = lazyCall("parseCSVToCollection", "components/collections/util/excel");

const ExcelField = forwardRef(({
  dataWrapper = true,
  isSingleImport = false,
  defaultFile = null,
  defaultSelectedSheet = null,
  defaultOffset = 0,
  ignoreColumns = false,
  referenceColumns = [],
  showAdvancedOptions = true,
  showPreview = true,
  rowLimit,
  setLoading = () => { },
  onLoadFile = () => { },
  onDownloadTemplate = () => { },
}, ref) => {

  const [file, setFile] = useState(defaultFile);
  const [selectedSheet, setSelectedSheet] = useState(defaultSelectedSheet);
  const [skipRows, setSkipRows] = useState(defaultOffset);
  const [sheets, setSheets] = useState([]);
  const [csv, setCsv] = useState(null);
  const [status, setStatus] = useState(null);
  const [data, setData] = useState(null);

  const loadExcelFile = async (file) => {

    if (file) {

      if (file?.url?.endsWith(".csv")) {
        const csv = await loadCSVFromUrl(file.url);
        setCsv(csv);
      }
      else if (file?.url?.endsWith(".xls") || file?.url?.endsWith(".xlsx")) {

        const workbook = await loadWorkbookFromUrl(file.url);
        if (!workbook) return;

        const sheetNames = await getSheetNames(workbook);
        if (sheetNames.length == 1) {
          setSelectedSheet(sheetNames[0]);
          setSheets([]);
        }
        else {
          setSelectedSheet(selectedSheet || sheetNames[0]);
          setSheets(sheetNames);
        }

      }

    }

  }

  const loadExcelFileFromSheet = async (file, selectedSheet) => {

    if (!selectedSheet) return;
    if (!file) return;

    const workbook = await loadWorkbookFromUrl(file.url);
    if (!workbook) return;

    try {
      const csv = await parseSheetToCSV(workbook, selectedSheet);
      setCsv(csv);
    }
    catch (err) {
      setData({
        offset: skipRows,
        sheet: selectedSheet,
        file,
        importedColumns: [],
        importedRows: [],
        rawRows: [],
        hasSameReferenceColumns: false,
        error: "Sheet not found"
      });
    }

  }

  const loadExcelValues = async () => {

    if (!csv) return;

    let hasSameReferenceColumns = true;
    let { columns: importedColumns, rows: importedRows } = await parseCSVToCollection({ csv, dataWrapper, offset: skipRows });
    let { rows: rawRows } = await parseCSVToCollection({ csv, dataWrapper, columns: referenceColumns, offset: skipRows });
    rawRows = rawRows.slice(1);

    importedColumns = importedColumns.filter((c) => !c.internal);

    for (let i = 0; i < Math.max(importedColumns.length, referenceColumns.length); i++) {

      const importedColumn = importedColumns[i];
      const filteredColumn = referenceColumns[i];

      if (!importedColumn || !filteredColumn) {
        hasSameReferenceColumns = false;
        break;
      }

      if (importedColumn?.label != filteredColumn?.label) {
        hasSameReferenceColumns = false;
        break;
      }

    }

    // if (importedRows.length == 0)
    //   return setStatus({ type: "danger", message: l("No se encontraron filas en el archivo%CA097") });
    if (rowLimit && rawRows.length > rowLimit)
      return setStatus({
        type: "danger",
        message: l("El archivo tiene más de {rows} registros. Por favor, divídelo en varios archivos%CA098", { rows: rowLimit })
      });

    let error = null;
    if (importedRows.length == 0 && rawRows.length == 0)
      error = "No data found";
    else if (referenceColumns?.length > 0 && importedColumns.length != referenceColumns.length)
      error = "Column count does not match";

    setData({
      offset: skipRows,
      sheet: selectedSheet,
      file,
      importedColumns,
      importedRows,
      rawRows,
      hasSameReferenceColumns,
      error
    });

  }

  useEffect(() => {
    loadExcelFile(file);
  }, [file]);

  useEffect(() => {
    loadExcelFileFromSheet(file, selectedSheet);
  }, [file, selectedSheet]);

  useEffect(() => {
    loadExcelValues();
  }, [csv, sheets, skipRows]);

  useEffect(() => {
    if (data) onLoadFile(data);
  }, [data]);

  useImperativeHandle(ref, () => ({
    status,
    setStatus: (status) => setStatus(status),
    setOffset: (offset) => setSkipRows(offset)
  }));

  return <>

    {
      isSingleImport && <>

        <div className="mt-1">
          <div class="form-group mb-0">
            <FileField
              name="file"
              id="file"
              required={true}
              class="form-control form-control-lg"
              accept=".csv, .xls, .xlsx"
              setFieldValue={() => { }}
              onCompleted={(data) => setFile(data)}
              setLoading={setLoading}
            />
            {
              !data &&
              <input
                type="file"
                name={"excel_field_error"}
                value={data?.toString() || ""}
                required
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  opacity: 0,
                  zIndex: -1,
                  cursor: "pointer"
                }}
              />
            }
          </div>
        </div>

        {
          sheets.length > 0 && <>
            <div className="mt-3">
              <div>
                <span className="h5 font-weight-light">
                  {l("Hoja a cargar%CA106")}
                </span>
              </div>
            </div>
            <div className="mt-2">
              <div class="form-group mb-1">
                <select
                  className="form-control form-control-sm"
                  value={selectedSheet}
                  onChange={(e) => setSelectedSheet(e.target.value)}
                >
                  {
                    sheets.map((sheet, i) => <option key={i} value={sheet}>{sheet}</option>)
                  }
                </select>
              </div>
            </div>
          </>
        }

        {
          showAdvancedOptions && file &&
          <>
            <button
              type="button"
              className="btn btn-link mt-2 m-0 p-0"
              data-toggle="collapse"
              data-target="#excel-advanced-options"
              aria-expanded="false"
              aria-controls="excel-advanced-options"
            >
              {l("Opciones avanzadas%SA220")} <i className="fas fa-chevron-down"></i>
            </button>

            <div
              id="excel-advanced-options"
              className="collapse"
            >

              {
                file &&
                <div className="mt-2">
                  <div>
                    <span className="h5 font-weight-light">
                      {l("Indica las filas a ignorar%VF078")}
                    </span>
                  </div>
                  <div className="mt-2">
                    <div class="form-group mb-2">
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        value={skipRows}
                        onChange={(e) => setSkipRows(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              }

            </div>
          </>
        }

      </>
    }

    {
      !isSingleImport && <>
        <div className="h5 text-primary mb-3">
          {l("Instrucciones%CA102", null, "AA")}
        </div>
        <div>
          <div>
            <span className="h5 text-primary mr-1">
              1.
            </span>
            <span className="text-dark">
              {l("Descarga tu datos actuales y actualízalos%CA103")}
              <small style={{ color: "#aaa", fontSize: "0.8rem" }}> ({l("Opcional%GA024")})</small>
            </span>
          </div>
          <div>
            <button
              type="button"
              class="btn btn-outline-primary btn-sm mt-2"
              onClick={onDownloadTemplate}
            >
              {l("Descargar%GA027")}
            </button>
          </div>
        </div>
        <div className="mt-3">
          <div>
            <span className="h5 text-primary mr-1">
              2.
            </span>
            <span className="text-dark">
              {l("Sube el archivo Excel%CA105")}
            </span>
          </div>
        </div>
        <div className="mt-2 pb-2">
          <div>
            <div class="form-group mb-0">
              <FileField
                name="file"
                id="file"
                required={true}
                class="form-control form-control-lg"
                accept=".csv, .xls, .xlsx"
                setFieldValue={() => { }}
                onCompleted={(data) => setFile(data)}
              />
            </div>
          </div>
        </div>

        {
          sheets.length > 0 && <>
            <div className="mt-2">
              <div>
                <span className="h5 font-weight-light">
                  {l("Hoja a cargar%CA106")}
                </span>
              </div>
            </div>
            <div className="mt-2">
              <div class="form-group mb-1">
                <select
                  className="form-control form-control-sm"
                  value={selectedSheet}
                  onChange={(e) => setSelectedSheet(e.target.value)}
                >
                  {
                    sheets.map((sheet, i) => <option key={i} value={sheet}>{sheet}</option>)
                  }
                </select>
              </div>
            </div>
          </>
        }

        {
          (file || sheets.length > 0) &&
          <button
            type="button"
            className="btn btn-link mt-2 m-0 p-0"
            data-toggle="collapse"
            data-target="#excel-advanced-options"
            aria-expanded="false"
            aria-controls="excel-advanced-options"
          >
            {l("Opciones avanzadas%SA220")} <i className="fas fa-chevron-down"></i>
          </button>
        }

        <div
          id="excel-advanced-options"
          className="collapse"
        >

          {
            file &&
            <div className="mt-2">
              <div>
                <span className="h5 text-primary mr-1">
                  4.
                </span>
                <span className="h5 font-weight-light">
                  {l("Indica las filas a ignorar%VF078")}
                </span>
              </div>
              <div className="mt-2">
                <div class="form-group mb-0">
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    onChange={(e) => setSkipRows(e.target.value)}
                    value={skipRows}
                  />
                </div>
              </div>
            </div>
          }

        </div>

      </>
    }

    {
      status && <div className={`alert alert-${status.type} mt-3`} role="alert">
        <i className={`fa fa-${status.type == "success" ? "check" : "warning"}`}></i> {status.message}
      </div>
    }

    {
      status?.logs?.length > 0 && <div>
        {
          status.logs.map((log, i) => <div key={`${log.message}-${i}`}>
            <div className={`text-${log.type == "success" ? "success" : "danger"}`}>
              <i className={`fa fa-${log.type == "success" ? "check" : "exclamation"}`}></i> {log.message}
            </div>
          </div>)
        }
      </div>
    }

    {
      showPreview && data && (data?.importedRows?.length > 0 || data?.rawRows?.length > 0) &&
      <div className="w-100 overflow-auto mt-2">
        <Table
          showExport={false}
          showFilters={false}
          showPagination={false}
          showForm={false}
          showImport={false}
          showSearch={false}
          isReadOnly={true}
          structure={ignoreColumns && referenceColumns?.length > 0 ? referenceColumns : data.importedColumns}
          values={ignoreColumns && referenceColumns?.length > 0 ? data.rawRows : data.importedRows}
        />
      </div>
    }

  </>;

});

ExcelField.displayName = "ExcelField";

export default ExcelField;